import { useState, useEffect, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/Domain/components/Shared/CheckBox/Checkbox';
import { Input } from '@/Domain/components/Shared/Input/Input';

type Props = {
  value?: string;
  onChange?: (value: { useHtmlSelector: boolean; selector: string }) => void;
  reset?: boolean;
  onResetComplete?: () => void;
  error?: boolean;
};

export const HtmlSelectorOption = ({
  value = '',
  onChange,
  reset,
  onResetComplete,
  error,
}: Props) => {
  const { t } = useTranslation(['knowledge_base']);
  const [useHtmlSelector, setUseHtmlSelector] = useState(false);
  const [selector, setSelector] = useState(value);

  useEffect(() => {
    if (onChange) {
      onChange({ useHtmlSelector, selector });
    }
  }, [useHtmlSelector, selector]);

  useEffect(() => {
    if (reset) {
      setUseHtmlSelector(false);
      setSelector('');
      if (onResetComplete) {
        onResetComplete();
      }
    }
  }, [reset, onResetComplete]);

  const handleUseHtmlSelectorChange = (checked: boolean) => {
    setUseHtmlSelector(checked === true);
  };

  const handleSelectorChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelector(e.target.value);
  };

  return (
    <div className="flex items-start gap-2">
      <Checkbox
        id="html-selector"
        checked={useHtmlSelector}
        onCheckedChange={handleUseHtmlSelectorChange}
      />
      <div className="flex flex-col">
        <label htmlFor="html-selector" className="text-sm font-medium cursor-pointer">
          {t('modals.upload_url.html_selector.label')}
        </label>
        <p className="text-sm text-foreground-subtle">
          {t('modals.upload_url.html_selector.description')}
        </p>
        {useHtmlSelector && (
          <div className="mt-2">
            <Input
              placeholder={t('modals.upload_url.html_selector.placeholder')}
              value={selector}
              onChange={handleSelectorChange}
              error={(useHtmlSelector && selector.trim() === '') || !!error}
            />
            {useHtmlSelector && selector.trim() === '' && (
              <p className="text-red-500 text-sm mt-1">
                {t('modals.upload_url.html_selector.error_required')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
